import { FC, useEffect, useMemo, useState } from 'react';
import { useMergeEntry } from 'src/features/entries/hooks';
import { IEntry, IMatchingEntry } from 'src/ts/interfaces';
import AssignedSubmissionRow from './AssignedSubmissionRow';
import CustomPagination from './CustomPagination';
import MatchesDataGrid from './MatchesDataGrid';
import {
  AssignedSubmissionSection,
  CloseButton,
  CloseModalButton,
  ErrorMessageAbsolute,
  MergeButton,
  ModalContainer,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  OtherSubmissionsSection
} from './styles';

interface PotentialMatchesScreenProps {
  matches: IMatchingEntry[];
  thisSubmission: IEntry;
  onDismiss: () => void;
  onRefreshMatches?: () => void;
}

const PotentialMatchesScreen: FC<PotentialMatchesScreenProps> = ({
  matches,
  thisSubmission,
  onDismiss,
  onRefreshMatches
}) => {
  const [selectedMatches, setSelectedMatches] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(5);
  const { onMergeEntry } = useMergeEntry({ navigateAway: false });

  const sortedMatches = useMemo(() => {
    return [...matches].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
  }, [matches]);

  const paginatedMatches = useMemo(() => {
    return sortedMatches.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  }, [sortedMatches, currentPage, pageSize]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const onSelectionChanged = (selectedIds: string[]) => {
    setSelectedMatches(new Set(selectedIds));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleMergeSubmissions = async () => {
    if (selectedMatches.size === 0) {
      setError('Please select at least one submission to merge.');
      return;
    }

    try {
      const selectedEntryIds = Array.from(selectedMatches);

      for (const selectedEntryId of selectedEntryIds) {
        await onMergeEntry(selectedEntryId, thisSubmission.id);
      }

      if (onRefreshMatches) {
        onRefreshMatches();
      }

      onDismiss();
    } catch (err) {
      setError('Failed to merge entries. Please try again.');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onDismiss();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer key={`modal-${thisSubmission.id}`}>
        <ModalHeader>
          <div>
            <ModalTitle>Merge Matching Submissions</ModalTitle>
            <ModalDescription>
              These submissions may be from the same applicant. Review the details, select any
              matches, and merge them into the same folder.
            </ModalDescription>
          </div>
          <CloseButton type="button" onClick={onDismiss}>
            <img src="/assets/images/close.svg" alt="Close" />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <AssignedSubmissionSection>
            <h3>This assigned submission</h3>
            <AssignedSubmissionRow submission={thisSubmission} />
          </AssignedSubmissionSection>

          <OtherSubmissionsSection>
            <h3>Other submissions</h3>
            <div>
              <MatchesDataGrid matches={paginatedMatches} onSelectionChanged={onSelectionChanged} />

              <CustomPagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={sortedMatches.length}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </OtherSubmissionsSection>
        </ModalContent>

        <ModalFooter>
          {error && <ErrorMessageAbsolute>{error}</ErrorMessageAbsolute>}
          <CloseModalButton type="button" onClick={onDismiss}>
            Close
          </CloseModalButton>
          <MergeButton
            type="button"
            onClick={handleMergeSubmissions}
            disabled={selectedMatches.size === 0}
            id="merge-submissions-button"
          >
            Merge Submissions →
          </MergeButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default PotentialMatchesScreen;
